import React from 'react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import api from '../../utils/api';
import { Home, LogIn, ShieldCheck, Users, CheckCircle2, FileCheck, Eye, EyeOff } from 'lucide-react';

const css = `
:root {
  --af-bg: #f8f9fc;
  --af-panel: #ffffff;
  --af-card: #ffffff;
  --af-text: #0f172a;
  --af-muted: #64748b;
  --af-primary: #c8102e;
  --af-primary-hover: #a50d26;
  --af-accent: #0033a0;
  --af-accent-2: #001f6b;
  --af-border: rgba(15, 23, 42, 0.08);
  --af-border-strong: rgba(15, 23, 42, 0.16);
  --af-shadow: 0 25px 70px -25px rgba(15, 23, 42, 0.15);
  --af-red-glow: rgba(200, 16, 46, 0.12);
  --af-blue-glow: rgba(0, 51, 160, 0.12);
}

* { box-sizing: border-box; }

.af-root {
  min-height: 100vh;
  width: 100%;
  background:
    radial-gradient(1000px 500px at 0% 0%, var(--af-red-glow), transparent 60%),
    radial-gradient(900px 500px at 100% 100%, var(--af-blue-glow), transparent 60%),
    linear-gradient(180deg, #ffffff 0%, var(--af-bg) 100%);
  color: var(--af-text);
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  position: relative;
  overflow: hidden;
}

.af-root::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 23, 42, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at 50% 50%, black 30%, transparent 80%);
  pointer-events: none;
}

/* Left Panel */
.af-promo {
  position: relative;
  padding: 56px 64px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-right: 1px solid var(--af-border);
  overflow: hidden;
}

.af-glow {
  position: absolute;
  width: 480px;
  height: 480px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(200, 16, 46, 0.16), transparent 60%);
  filter: blur(50px);
  top: -120px;
  left: -120px;
  animation: af-pulse 10s ease-in-out infinite alternate;
}
.af-glow.alt {
  background: radial-gradient(circle, rgba(0, 51, 160, 0.14), transparent 60%);
  top: auto;
  bottom: -160px;
  left: auto;
  right: -140px;
  animation-delay: -5s;
}

@keyframes af-pulse {
  0% { transform: translate(0, 0) scale(1); opacity: 0.8; }
  100% { transform: translate(24px, 16px) scale(1.05); opacity: 1; }
}

.af-brand {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  z-index: 2;
}

.af-brand__mark {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
}

.af-brand__mark img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.af-brand__name {
  font-weight: 700;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: var(--af-text);
}

.af-brand__sub {
  color: var(--af-muted);
  font-size: 12px;
  margin-top: 2px;
}

.af-hero {
  position: relative;
  z-index: 2;
  max-width: 540px;
}

.af-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid var(--af-border-strong);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--af-muted);
  margin-bottom: 22px;
  backdrop-filter: blur(6px);
}

.af-tag__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--af-accent);
  box-shadow: 0 0 0 4px rgba(0, 51, 160, 0.15);
}

.af-headline {
  font-family: 'Fraunces', 'Playfair Display', Georgia, serif;
  font-size: clamp(42px, 5vw, 62px);
  line-height: 1.05;
  letter-spacing: -0.03em;
  font-weight: 500;
  margin: 0 0 20px;
  color: var(--af-text);
}

.af-headline em {
  font-style: italic;
  color: var(--af-accent);
}

.af-sub {
  color: var(--af-muted);
  font-size: 16px;
  line-height: 1.65;
  max-width: 470px;
  margin: 0 0 36px;
}

.af-features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  max-width: 520px;
}

.af-feature {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid var(--af-border);
  border-radius: 14px;
  backdrop-filter: blur(6px);
  transition: all 0.2s ease;
}

.af-feature:hover {
  border-color: var(--af-border-strong);
  transform: translateY(-2px);
  box-shadow: 0 10px 24px -16px rgba(15, 23, 42, 0.2);
}

.af-feature__ic {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(0, 51, 160, 0.1);
  color: var(--af-accent);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.af-feature__t {
  font-size: 13px;
  font-weight: 700;
  color: var(--af-text);
  margin-bottom: 3px;
}

.af-feature__d {
  font-size: 12px;
  color: var(--af-muted);
  line-height: 1.5;
}

.af-stats {
  position: relative;
  z-index: 2;
  display: flex;
  gap: 40px;
  padding-top: 28px;
  border-top: 1px solid var(--af-border);
}

.af-stat__num {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--af-text);
}

.af-stat__lbl {
  display: block;
  font-size: 11px;
  color: var(--af-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 4px;
  font-weight: 600;
}

/* Right Panel */
.af-form-panel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 48px;
}

.af-home {
  position: absolute;
  top: 28px;
  right: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--af-muted);
  font-size: 13px;
  text-decoration: none;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid var(--af-border);
  background: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
  font-weight: 500;
}

.af-home:hover {
  color: var(--af-text);
  border-color: var(--af-border-strong);
  background: #ffffff;
}

.af-card {
  width: 100%;
    max-width: 500px;
  border-radius: 22px;
  padding: 44px;
  box-shadow: var(--af-shadow);
  position: relative;
}

.af-password-wrap {
  position: relative;
  margin-bottom: 8px;
}

.af-password-wrap input {
  margin-bottom: 0 !important;
  padding-right: 44px !important;
}

.af-password-toggle {
  position: absolute;
  top: 50%;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  transform: translateY(-50%);
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--af-muted);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.af-password-toggle:hover {
  background: rgba(15, 23, 42, 0.06);
  color: var(--af-text);
}

.af-password-toggle:focus-visible {
  outline: 2px solid var(--af-accent);
  outline-offset: 2px;
}

.af-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--af-primary), var(--af-accent));
  border-radius: 22px 22px 0 0;
}

.af-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(0, 51, 160, 0.08);
  color: var(--af-accent);
  border: 1px solid rgba(0, 51, 160, 0.18);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
}

.af-title {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 10px;
  color: var(--af-text);
}

.af-desc {
  color: var(--af-muted);
  font-size: 14px;
  margin: 0 0 30px;
  line-height: 1.6;
}

.af-sso-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 15px 20px;
  background: linear-gradient(135deg, var(--af-primary) 0%, var(--af-primary-hover) 100%);
  color: white;
  border: none;
  border-radius: 13px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 28px -8px rgba(200, 16, 46, 0.35);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.af-sso-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.af-sso-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px -10px rgba(200, 16, 46, 0.45);
}

.af-sso-btn:hover::before {
  transform: translateX(100%);
}

.af-sso-btn:active {
  transform: translateY(0);
}

.af-ms-logo {
  width: 18px;
  height: 18px;
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 2px;
}

.af-ms-logo span { display: block; border-radius: 1px; }
.af-ms-logo span:nth-child(1) { background: #f25022; }
.af-ms-logo span:nth-child(2) { background: #7fba00; }
.af-ms-logo span:nth-child(3) { background: #00a4ef; }
.af-ms-logo span:nth-child(4) { background: #ffb900; }

.af-hint {
  text-align: center;
  color: var(--af-muted);
  font-size: 13px;
  line-height: 1.6;
  margin: 18px 0 0;
}

.af-divider {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 26px 0;
  color: var(--af-muted);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 600;
}

.af-divider::before,
.af-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--af-border-strong), transparent);
}

.af-footer {
  margin-top: 26px;
  text-align: center;
  font-size: 12px;
  color: var(--af-muted);
  line-height: 1.6;
}

.af-footer a {
  color: var(--af-accent);
  text-decoration: none;
  font-weight: 600;
}

.af-footer a:hover {
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 1024px) {
  .af-root { grid-template-columns: 1fr; }
  .af-promo {
    padding: 44px 32px 36px;
    border-right: none;
    border-bottom: 1px solid var(--af-border);
    min-height: auto;
  }
  .af-headline { font-size: 44px; }
  .af-stats { gap: 32px; }
  .af-form-panel { padding: 36px 24px 48px; }
}

@media (max-width: 640px) {
  .af-features { grid-template-columns: 1fr; }
  .af-card { padding: 32px 24px; border-radius: 18px; }
  .af-card::after { border-radius: 18px 18px 0 0; }
  .af-headline { font-size: 38px; }
  .af-stats { flex-wrap: wrap; gap: 20px; padding-top: 22px; }
  .af-home { top: 16px; right: 16px; }
}
`;

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState(null);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSSOLogin = () => {
    const currentOrigin = window.location.origin;
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendBaseUrl = isLocalDev
      ? `${currentOrigin.replace(/:\d+$/, '')}:5000`
      : currentOrigin;
    const backendAuthUrl = `${backendBaseUrl}/api/auth/sso/authorize`;
    window.location.href = backendAuthUrl;
  };

  const handlePasswordLogin = async (selectedRole = null) => {
    if (!email || !password) return alert('Enter email and password');
    setLoading(true);
    try {
      const payload = { email, password };
      if (selectedRole) payload.role = selectedRole;
      const res = await api.post('/auth/login', payload);
      // On success, store token and redirect
      const token = res.data.token;
      if (token) {
        localStorage.setItem('artflow_token', token);
        localStorage.setItem('artflow_type', 'user');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.requiresRole) {
        setRoleOptions(err.response.data.roles || []);
      } else {
        alert(err.response?.data?.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: <ShieldCheck size={16} />, title: '4-Stage Workflow', desc: 'NAFDAC to colour proof, tracked end-to-end.' },
    { icon: <Users size={16} />, title: '5 Review Teams', desc: 'Structured hand-offs across departments.' },
    { icon: <FileCheck size={16} />, title: 'Full Audit Trail', desc: 'Every action logged and traceable.' },
    { icon: <CheckCircle2 size={16} />, title: 'Supplier Ready', desc: 'Onboard and coordinate suppliers cleanly.' },
  ];

  return (
    <div className="af-root">
      <style>{css}</style>

      {/* Left Panel */}
      <aside className="af-promo">
        <div className="af-glow" />
        <div className="af-glow alt" />

        <div className="af-brand">
          <div className="af-brand__mark">
            <img src="/chivita.png" alt="Chivita logo" />
          </div>
          <div>
            <div className="af-brand__name">CHI Laminate Artflow Approval</div>
            <div className="af-brand__sub">Enterprise Packaging Workflow</div>
          </div>
        </div>

        <div className="af-hero">
          <span className="af-tag">
            <span className="af-tag__dot" />
            Admin Portal
          </span>
          <h1 className="af-headline">
            Laminate<br /><em>Artflow.</em>
          </h1>
          <p className="af-sub">
            End-to-end packaging artwork approval — from NAFDAC review to supplier colour proof,
            orchestrated in one controlled, auditable workflow.
          </p>

          <div className="af-features">
            {features.map((f, i) => (
              <div className="af-feature" key={i}>
                <div className="af-feature__ic">{f.icon}</div>
                <div>
                  <div className="af-feature__t">{f.title}</div>
                  <div className="af-feature__d">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="af-stats">
          <div>
            <div className="af-stat__num">4</div>
            <span className="af-stat__lbl">Approval Stages</span>
          </div>
          <div>
            <div className="af-stat__num">5</div>
            <span className="af-stat__lbl">Review Teams</span>
          </div>
          <div>
            <div className="af-stat__num">100%</div>
            <span className="af-stat__lbl">Traceable</span>
          </div>
        </div>
      </aside>

      {/* Right Panel */}
      <main className="af-form-panel">
        <Link to="/" className="af-home">
          <Home size={14} />
          Back to home
        </Link>

        <div className="af-card">
          <span className="af-badge">
            <ShieldCheck size={12} /> Admin Access
          </span>
          <h2 className="af-title">Sign in to Artflow</h2>
          <p className="af-desc">
            Use your company Microsoft account or sign in using email and password.
          </p>

          <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
            <button type="button" className="af-sso-btn" onClick={handleSSOLogin} style={{ justifyContent: 'center' }}>
              <span className="af-ms-logo" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </span>
              Sign in with Microsoft
            </button>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ width: 'auto', minWidth: 260, textAlign: 'center' }}
                onClick={() => setShowEmailLogin(prev => !prev)}
              >
                {showEmailLogin ? 'Hide email and password login' : 'Sign in using email and password'}
              </button>
            </div>
          </div>

          {showEmailLogin && (
            <div style={{ marginBottom: 16 }}>
              <input
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{ width:'100%', padding:'10px 12px', marginBottom:8, borderRadius:8, border:'1px solid var(--af-border)' }}
              />
              <div className="af-password-wrap">
                <input
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{ width:'100%', padding:'10px 12px', marginBottom:8, borderRadius:8, border:'1px solid var(--af-border)' }}
                />
                <button
                  type="button"
                  className="af-password-toggle"
                  onClick={() => setShowPassword(prev => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {roleOptions ? (
                <div style={{ marginBottom: 8 }}>
                  <p style={{ margin: 0, marginBottom: 6 }}>Select role to sign in as:</p>
                  {roleOptions.map(r => (
                    <button key={r.role} className="af-sso-btn" style={{ marginBottom: 6 }} onClick={() => handlePasswordLogin(r.role)}>
                      {r.label || r.role}
                    </button>
                  ))}
                  <button className="btn btn-ghost btn-sm" onClick={() => setRoleOptions(null)}>Cancel</button>
                </div>
              ) : (
                <button className="af-sso-btn w-full" onClick={() => handlePasswordLogin()} disabled={loading}>
                  <LogIn size={16} /> Sign in with email
                </button>
              )}
            </div>
          )}

          <p className="af-hint">
            Single sign-on via Microsoft Entra ID.<br />
            You'll be redirected to your organization's login.
          </p>

          <div className="af-footer">
            Protected by enterprise SSO
          </div>
        </div>
      </main>
    </div>
  );
}
